import * as React from 'react';
import { useState, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import groupBy from 'lodash/groupBy';

import fetch from 'logic/rest/FetchProvider';
import type { InvestigationsDetailsAPIType } from 'security-app/hooks/api/investigationsAPI.types';
import { adjustFormat } from 'util/DateTime';
import EvidenceOverview from 'security-app/components/Investigations/Details/TimelineSegment/EvidenceOverview/EvidenceOverview';
import { Alert } from 'components/bootstrap';
import type {
  Evidences,
  EvidenceFilters,
  EvidenceTimeRangeFilter,
  Evidence,
  EventEvidence,
  LogEvidence,
} from 'security-app/components/Investigations/Details/TimelineSegment/types';
import {
  typeAllFilter,
  severityAllFilter,
  EVIDENCE_CAROUSEL_ID,
  evidenceTypeLog,
  evidenceTypeEvent,
} from 'security-app/components/Investigations/Details/TimelineSegment/Constants';
import useSendTelemetry from 'logic/telemetry/useSendTelemetry';
import { TELEMETRY_EVENT_TYPE } from 'telemetry/Constants';
import { qualifyUrl } from 'util/URLUtils';
import { CarouselProvider, useCarouselApi } from 'components/common/Carousel';
import { defaultCompare } from 'logic/DefaultCompare';
import { isLogEvidence } from 'security-app/components/Investigations/Details/TimelineSegment/Utils';
import { evidenceCardId } from 'security-app/components/Investigations/Details/TimelineSegment/EvidenceOverview/EvidenceCardFoundation';
import MessageSummaryTemplateProvider from 'search/contexts/MessageSummaryTemplateProvider';

import TimelineChart from './TimelineChart';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  height: 100%;
`;

const initialTimeLineRange = (evidences: Evidences | undefined = []) => {
  if (!evidences.length) {
    return null;
  }

  const evidenceTimestamps = evidences.map((evidence) => new Date(evidence.timestamp).getTime());
  const startDate = Math.min(...evidenceTimestamps);
  const endDate = Math.max(...evidenceTimestamps);

  const difference = endDate - startDate;
  const buffer = difference * 0.05; // display a "padding" of 5% for start and end of bar chart

  return {
    from: adjustFormat(new Date(startDate - buffer), 'internal'),
    to: adjustFormat(new Date(endDate + buffer), 'internal'),
  };
};

const formatEventsEvidence = (events: InvestigationsDetailsAPIType['events'] = []): Array<EventEvidence> =>
  events.map((event) => ({
    timestamp: event.timestamp,
    id: event.id,
    type: evidenceTypeEvent,
    entity: event,
  }));

const formatLogsEvidence = (logs: InvestigationsDetailsAPIType['logs'] = []): Array<LogEvidence> =>
  logs.map((log) => ({
    timestamp: log.timestamp,
    id: log.id,
    type: evidenceTypeLog,
    entity: log,
  }));

const evidenceRangeMatches = (evidenceTimestamp: string, timeRangeFilter: EvidenceTimeRangeFilter) => {
  const timestamp = new Date(evidenceTimestamp).getTime();

  return timestamp >= new Date(timeRangeFilter.from).getTime() && timestamp <= new Date(timeRangeFilter.to).getTime();
};

const evidenceTypeMatches = (evidenceType: string, typeFilter: string) => {
  if (typeFilter === typeAllFilter) {
    return true;
  }

  return evidenceType === typeFilter;
};

const evidenceSeverityMatches = (evidence: Evidence, severityFilter: string) => {
  if (severityFilter === severityAllFilter) {
    return true;
  }

  if (isLogEvidence(evidence)) {
    return false;
  }

  return (
    evidence.entity.fields &&
    'event_severity' in evidence.entity.fields &&
    evidence.entity.fields.event_severity === severityFilter
  );
};

const evidenceHasSimilarFields = (evidence: Evidence, similarFieldsFilter: EvidenceFilters['similar']) => {
  if (!similarFieldsFilter) {
    return true;
  }

  if (evidence.id === similarFieldsFilter.targetEvidence.id) {
    return true;
  }

  if (isLogEvidence(evidence) && !!similarFieldsFilter.fields.logs.find(({ message: { id } }) => id === evidence.id)) {
    return true;
  }

  return !!similarFieldsFilter.fields.events.find(({ event: { id } }) => id === evidence.id);
};

const useEvidencesGroupedByTime = (evidences: Array<Evidence>, timeRangeFilter: EvidenceFilters['timeRange']) =>
  useMemo(() => {
    const evidenceInCarousel = evidences?.filter((evidence) =>
      evidenceRangeMatches(evidence.timestamp, timeRangeFilter),
    );
    const groupedEvidences = groupBy(evidenceInCarousel, ({ timestamp }) =>
      adjustFormat(timestamp, 'dateHourAndMinute'),
    ) as Record<string, Array<Evidence>>;

    return {
      evidencesGroupedByTime: Object.entries(groupedEvidences).sort((groupA, groupB) =>
        defaultCompare(groupA[0], groupB[0]),
      ),
      evidencesCount: evidenceInCarousel.length,
    };
  }, [evidences, timeRangeFilter]);

type Props = {
  events: InvestigationsDetailsAPIType['events'];
  logs: InvestigationsDetailsAPIType['logs'];
  investigationId: string;
};

const TimelineSegment = ({ events, logs, investigationId }: Props) => {
  const sendTelemetry = useSendTelemetry();
  const carouselApi = useCarouselApi(EVIDENCE_CAROUSEL_ID);

  const evidences = useMemo(
    () =>
      [...formatEventsEvidence(events), ...formatLogsEvidence(logs)].sort((evidenceA, evidenceB) =>
        defaultCompare(evidenceA.timestamp, evidenceB.timestamp),
      ),
    [events, logs],
  );

  const initialTimelineRange = initialTimeLineRange(evidences);
  const [filters, setFilters] = useState<EvidenceFilters>({
    timeRange: initialTimelineRange,
    type: typeAllFilter,
    severity: severityAllFilter,
  });

  // Do not consider range filter for evidence in timeline,
  // this way users see evidence directly when they change the range in the timeline.
  const allEvidenceInTimeline = useMemo(
    () =>
      evidences?.filter(
        (evidence) =>
          evidenceHasSimilarFields(evidence, filters.similar) &&
          evidenceTypeMatches(evidence.type, filters.type) &&
          evidenceSeverityMatches(evidence, filters.severity),
      ),
    [evidences, filters.severity, filters.similar, filters.type],
  );

  // Only show evidence in carousel which is currently visible in timeline
  const { evidencesGroupedByTime, evidencesCount } = useEvidencesGroupedByTime(
    allEvidenceInTimeline,
    filters.timeRange,
  );

  const _setFilters = useCallback(
    (newFilters: Partial<EvidenceFilters>) => {
      sendTelemetry(TELEMETRY_EVENT_TYPE.SECURITY_APP.INVESTIGATION_TIMELINE_FILTER_UPDATED, {
        app_pathname: 'security',
        app_section: 'investigation',
        event_details: newFilters,
      });

      setFilters((cur) => ({ ...cur, ...newFilters }));
    },
    [sendTelemetry],
  );

  const showSimilar = useCallback(
    (targetEvidence: Evidence) => {
      const urlSuffix = isLogEvidence(targetEvidence)
        ? `message/${targetEvidence.entity.index}/${targetEvidence.entity.id}`
        : `event/${targetEvidence.entity.id}`;
      const url = qualifyUrl(
        `/plugins/org.graylog.plugins.securityapp.investigations/investigations/evidence/${investigationId}/similar/${urlSuffix}`,
      );

      return fetch('GET', url).then((res) => {
        _setFilters({
          similar: {
            targetEvidence,
            fields: res,
          },
          timeRange: initialTimelineRange,
          severity: severityAllFilter,
          type: typeAllFilter,
        });
      });
    },
    [_setFilters, initialTimelineRange, investigationId],
  );

  const resetShowSimilar = useCallback(() => {
    _setFilters({
      similar: null,
    });
  }, [_setFilters]);

  const onMarkerClick = useCallback(
    (evidenceTimestamp: string) => {
      const evidenceGroupTimestamp = adjustFormat(evidenceTimestamp, 'dateHourAndMinute');
      const evidenceGroupIndex = evidencesGroupedByTime.findIndex(
        ([timestamp]) => timestamp === evidenceGroupTimestamp,
      );
      const evidenceGroup = evidencesGroupedByTime[evidenceGroupIndex];
      const evidence = evidenceGroup[1].find(
        ({ timestamp }) => adjustFormat(timestamp, 'internal') === adjustFormat(evidenceTimestamp, 'internal'),
      );

      carouselApi.scrollTo(evidenceGroupIndex, true);
      document.getElementById(evidenceCardId(evidence.id)).focus();
    },
    [carouselApi, evidencesGroupedByTime],
  );

  if (!evidences.length) {
    return <Alert>No events or messages have been added to this investigation yet.</Alert>;
  }

  return (
    <MessageSummaryTemplateProvider>
      <Container>
        <TimelineChart
          filters={filters}
          initialTimelineRange={initialTimelineRange}
          onClickMarker={onMarkerClick}
          evidences={allEvidenceInTimeline}
          evidencesGroupedByTime={evidencesGroupedByTime}
          setFilters={_setFilters}
        />
        <EvidenceOverview
          evidencesGroupedByTime={evidencesGroupedByTime}
          onShowSimilar={showSimilar}
          evidenceCount={evidencesCount}
          onResetShowSimilar={resetShowSimilar}
          similarFieldsFilter={filters.similar}
        />
      </Container>
    </MessageSummaryTemplateProvider>
  );
};

const TimelineWrapper = ({ events, logs, investigationId }: Props) => (
  <CarouselProvider
    carouselId={EVIDENCE_CAROUSEL_ID}
    options={{ align: 'start', inViewThreshold: 0.9, watchDrag: false }}>
    <TimelineSegment events={events} logs={logs} investigationId={investigationId} />
  </CarouselProvider>
);

export default TimelineWrapper;
